import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthProvider";
import axios from "axios";
import { baseUrl_SPB } from "../BaseUrl";

function AIInventoryCheck() {
  const authContext = useAuth();
  const [inventoryResponse, setInventoryResponse] = useState(null);
  const [dataToShow, setDataToShow] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUser() {
      try {
        const token = authContext.token;
        const res = await fetch(`${baseUrl_SPB}/api/v1/user/me`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        });
        const data = await res.json();
        let user = data;
        let inv = null;
        if (user.inventoryResponse) {
          let raw = typeof user.inventoryResponse === "string" ? user.inventoryResponse : JSON.stringify(user.inventoryResponse);
          raw = raw.replace(/```/g, "");
          raw = raw.replace(/json/g, "");
          inv = raw;
        } else if (user.scanSummary && user.detectedItems) {
          function stripBackticks(obj) {
            if (typeof obj === 'string') return obj.replace(/```/g, "");
            if (Array.isArray(obj)) return obj.map(stripBackticks);
            if (obj && typeof obj === 'object') {
              const out = {};
              for (const k in obj) out[k] = stripBackticks(obj[k]);
              return out;
            }
            return obj;
          }
          inv = stripBackticks(user);
        }
        setInventoryResponse(inv);
        setParseError(null);
      } catch (e) {
        setParseError(e);
        setInventoryResponse(null);
      } finally {
        setLoading(false);
      }
    }
    fetchUser();
  }, [authContext.token]);

  useEffect(() => {
    // Parse and set dataToShow when inventoryResponse changes
    let parsed = inventoryResponse;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch (e) {
        parsed = null;
      }
    }
    if (!parsed) {
      parsed = {
        scanSummary: {
          vehicleType: "van",
          detectedTrade: "general contractor",
          totalDetectedItems: 2,
          overallConfidence: 0.85
        },
        detectedItems: [
          {
            detectedItemName: "cleaning spray bottles",
            normalizedItemName: "cleaning spray",
            category: "consumables",
            subcategory: "cleaning",
            quantityDetected: 3,
            estimated: false,
            confidence: 0.9,
            storageLocation: "shelf",
            brand: "",
            notes: "",
            inventoryMatched: false,
            matchConfidence: 0.0,
            matchedInventoryItem: {
              itemNo: "",
              itemName: "",
              sku: "",
              bin: "",
              totalInStock: 0
            },
            stockStatus: "",
            unmatchedReason: "No similar item in inventory database"
          }
        ]
      };
    }
    setDataToShow(parsed);
  }, [inventoryResponse]);

  let warning = null;
  if (!dataToShow && !loading) {
    warning = (
      <div style={{ color: 'red', marginBottom: 16 }}>
        <b>Warning:</b> No inventoryResponse found for this user.<br/>
        {parseError && <span>Parse error: {parseError.message}</span>}
        <br/>Check the browser console for user info.
      </div>
    );
  }
  const scanSummary = dataToShow && typeof dataToShow === 'object' ? dataToShow.scanSummary : null;
  const detectedItems = dataToShow && typeof dataToShow === 'object' ? dataToShow.detectedItems : null;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <h2>AI Inventory Check</h2>
      {loading && <div>Loading user inventory...</div>}
      {warning}
      {!scanSummary && (
        <div style={{ color: 'red', marginBottom: 16 }}>
          <b>Error:</b> No scan summary found in inventory data.
        </div>
      )}
      {!detectedItems && (
        <div style={{ color: 'red', marginBottom: 16 }}>
          <b>Error:</b> No detected items found in inventory data.
        </div>
      )}
      <h2>AI Inventory Check</h2>
      <h3>Scan Summary</h3>
      <table style={{ borderCollapse: "collapse", marginBottom: 24 }}>
        <tbody>
          <tr><td style={{ fontWeight: 600, padding: 4 }}>Vehicle Type</td><td style={{ padding: 4 }}>{scanSummary?.vehicleType}</td></tr>
          <tr><td style={{ fontWeight: 600, padding: 4 }}>Detected Trade</td><td style={{ padding: 4 }}>{scanSummary?.detectedTrade}</td></tr>
          <tr><td style={{ fontWeight: 600, padding: 4 }}>Total Detected Items</td><td style={{ padding: 4 }}>{scanSummary?.totalDetectedItems}</td></tr>
          <tr><td style={{ fontWeight: 600, padding: 4 }}>Overall Confidence</td><td style={{ padding: 4 }}>{scanSummary?.overallConfidence}</td></tr>
        </tbody>
      </table>
      <h3>Detected Items</h3>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }} border={1}>
        <thead style={{ background: "#f7f7fa" }}>
          <tr>
            <th>Name</th>
            <th>Normalized Name</th>
            <th>Category</th>
            <th>Subcategory</th>
            <th>Quantity</th>
            <th>Estimated?</th>
            <th>Confidence</th>
            <th>Location</th>
            <th>Brand</th>
            <th>Notes</th>
            <th>Matched?</th>
            <th>Match Confidence</th>
            <th>Matched Item No</th>
            <th>Matched Item Name</th>
            <th>Matched SKU</th>
            <th>Matched Bin</th>
            <th>Matched Stock</th>
            <th>Stock Status</th>
            <th>Unmatched Reason</th>
          </tr>
        </thead>
        <tbody>
          {detectedItems && detectedItems.length > 0 ? detectedItems.map((item, idx) => (
            <tr key={idx}>
              <td>{item.detectedItemName}</td>
              <td>{item.normalizedItemName}</td>
              <td>{item.category}</td>
              <td>{item.subcategory}</td>
              <td>{item.quantityDetected}</td>
              <td>{item.estimated ? "Yes" : "No"}</td>
              <td>{item.confidence}</td>
              <td>{item.storageLocation}</td>
              <td>{item.brand}</td>
              <td>{item.notes}</td>
              <td>{item.inventoryMatched ? "Yes" : "No"}</td>
              <td>{item.matchConfidence}</td>
              <td>{item.matchedInventoryItem?.itemNo}</td>
              <td>{item.matchedInventoryItem?.itemName}</td>
              <td>{item.matchedInventoryItem?.sku}</td>
              <td>{item.matchedInventoryItem?.bin}</td>
              <td>{item.matchedInventoryItem?.totalInStock}</td>
              <td>{item.stockStatus}</td>
              <td>{item.unmatchedReason}</td>
            </tr>
          )) : (
            <tr><td colSpan={19} style={{ textAlign: "center", color: "#888" }}>No detected items.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default AIInventoryCheck;
